import React, { useState } from 'react';
import {
  IconArrowRight,
  IconCalendarEvent,
  IconCheck,
  IconLoader2,
  IconPlus,
} from '@tabler/icons-react';
import type { Assignment } from '../../../entities/assignment';
import { CreateTaskModal, CreateForm } from '../../create-task-modal/ui/CreateTaskModal';
import { QuickTaskModal, QuickPreset, QUICK_PRESETS } from './QuickTaskModal';
import type { MoscowPrefill } from '../../pin-moscow-form';
import dayjs from 'dayjs';
import { formatDateTime, getManagerColor, getManagerInitials } from '../../../shared/lib/utils';

interface Props {
  assignments: Assignment[];
  isLoading: boolean;
  prefill?: MoscowPrefill;
  onResolve: (id: string, text: string) => Promise<void>;
  onCreate: (data: { name: string; ownerId: string; planDate: string }) => Promise<void>;
}

const dateBadge = (planDate: string): { cls: string; label: string } => {
  const iso = planDate.trim().replace(' ', 'T');
  const withTz = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  const d = dayjs(withTz);
  const planDay = d.format('YYYY-MM-DD');
  const today = dayjs().format('YYYY-MM-DD');
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const time = d.format('HH:mm');
  const timeLabel = time === '23:59' ? 'Весь день' : time;
  if (planDay < today) return { cls: 'asgn-date-overdue', label: `Просрочено · ${timeLabel}` };
  if (planDay === today) return { cls: 'asgn-date-today', label: `Сегодня · ${timeLabel}` };
  if (planDay === tomorrow) return { cls: 'asgn-date-tomorrow', label: `Завтра · ${timeLabel}` };
  const dateLabel = d.format('DD.MM');
  return { cls: 'asgn-date-future', label: time === '23:59' ? `${dateLabel} · Весь день` : formatDateTime(planDate) };
};

const AssignmentsSection: React.FC<Props> = ({
  assignments,
  isLoading,
  prefill,
  onResolve,
  onCreate,
}) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [resolveTexts, setResolveTexts] = useState<Record<string, string>>({});
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [quickPreset, setQuickPreset] = useState<QuickPreset | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = (id: string) => {
    setOpenId(prev => (prev === id ? null : id));
  };

  const handleResolve = async (id: string) => {
    const text = (resolveTexts[id] ?? '').trim();
    if (!text) return;
    setResolvingId(id);
    try {
      await onResolve(id, text);
      setResolveTexts(prev => { const n = { ...prev }; delete n[id]; return n; });
      setOpenId(null);
    } finally {
      setResolvingId(null);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCreate = async (values: CreateForm) => {
    setIsSaving(true);
    try {
      await onCreate({
        name: values.name.trim(),
        ownerId: values.ownerId,
        planDate: values.planDate,
      });
      handleCloseModal();
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickCreate = async (data: { name: string; ownerId: string; planDate: string }) => {
    setIsSaving(true);
    try {
      await onCreate(data);
      setQuickPreset(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMoscowCreate = async (tasks: string[], ownerId: string, planDate: string) => {
    setIsSaving(true);
    try {
      for (const name of tasks) await onCreate({ name, ownerId, planDate });
      setQuickPreset(null);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="amo-tasks">
        <div className="asgn-section-loading">
          <IconLoader2 size={15} stroke={2} className="spin" />
          <span>Загрузка...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="amo-tasks">
      {assignments.length === 0 && (
        <div className="amo-tasks-empty">Нет активных задач</div>
      )}

      {assignments.map(a => {
        const { cls, label } = dateBadge(a.planDate);
        const isOpen = openId === a.id;
        const overdue = cls === 'asgn-date-overdue';
        const today = cls === 'asgn-date-today';

        return (
          <div
            className={`amo-task${isOpen ? ' amo-task-open' : ''}${overdue ? ' amo-task-overdue' : today ? ' amo-task-today' : ''}`}
            key={a.id}
            onClick={() => handleToggle(a.id)}
          >
            <div className="amo-task-circle" aria-hidden="true" />
            <div className="amo-task-body">
              <div className="amo-task-name">{a.name}</div>
              <div className="amo-task-foot">
                <div className="amo-feed-task-assign amo-task-assign">
                  {a.expand?.authorId && (
                    <div className="amo-feed-task-assign-user">
                      <div
                        className="amo-feed-avatar amo-feed-avatar-sm"
                        style={{ background: getManagerColor(a.authorId ?? '') }}
                      >
                        {getManagerInitials(a.expand.authorId.name, a.expand.authorId.email)}
                      </div>
                      <span>{a.expand.authorId.name}</span>
                    </div>
                  )}
                  {a.expand?.authorId && a.expand?.ownerId && (
                    <IconArrowRight size={11} stroke={2} className="amo-feed-task-assign-arrow" />
                  )}
                  {a.expand?.ownerId && (
                    <div className="amo-feed-task-assign-user">
                      <div
                        className="amo-feed-avatar amo-feed-avatar-sm"
                        style={{ background: getManagerColor(a.ownerId ?? '') }}
                      >
                        {getManagerInitials(a.expand.ownerId.name, a.expand.ownerId.email)}
                      </div>
                      <span>{a.expand.ownerId.name}</span>
                    </div>
                  )}
                </div>
                <div className="amo-task-dates">
                  <span className={`amo-task-date ${cls}`}>
                    <IconCalendarEvent size={10} stroke={2} />
                    {label}
                  </span>
                  <span className="amo-task-updated">Обновлено: {formatDateTime(a.updated)}</span>
                </div>
              </div>
              {isOpen && (
                <div className="amo-task-resolve" onClick={e => e.stopPropagation()}>
                  <textarea
                    className="amo-task-resolve-input"
                    name={`assignment-result-${a.id}`}
                    placeholder="Результат выполнения..."
                    value={resolveTexts[a.id] ?? ''}
                    onChange={e => setResolveTexts(prev => ({ ...prev, [a.id]: e.target.value }))}
                    rows={2}
                    autoFocus
                  />
                  <button
                    className="amo-task-resolve-btn"
                    type="button"
                    disabled={resolvingId === a.id || !(resolveTexts[a.id] ?? '').trim()}
                    onClick={() => handleResolve(a.id)}
                  >
                    {resolvingId === a.id
                      ? <IconLoader2 size={13} stroke={2} className="spin" />
                      : <IconCheck size={13} stroke={2.5} />}
                    Выполнить
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="amo-tasks-quick">
        {QUICK_PRESETS.map(p => (
          <button
            key={p.key}
            className="amo-tasks-quick-btn"
            type="button"
            onClick={() => setQuickPreset(p)}
          >
            <IconPlus size={12} stroke={2.5} />
            {p.label}
          </button>
        ))}
      </div>

      <button className="amo-tasks-add" type="button" onClick={handleOpenModal}>
        <IconPlus size={13} stroke={2.5} />
        Добавить задачу
      </button>

      <CreateTaskModal
        open={showModal}
        isSaving={isSaving}
        onClose={handleCloseModal}
        onCreate={handleCreate}
        showTaskSelect={false}
      />

      <QuickTaskModal
        preset={quickPreset}
        isSaving={isSaving}
        prefill={prefill}
        onClose={() => setQuickPreset(null)}
        onCreate={handleQuickCreate}
        onCreateMany={handleMoscowCreate}
      />
    </div>
  );
};

export { AssignmentsSection };
